import { Notification, type NotificationProps } from '@ets/design-system';
import { FormattedMessage } from 'react-intl';
import { type ExternalToast, toast as T } from 'sonner';

type IntlToastProps = {
  title: string;
  titleValues?: Record<string, string>;
  message?: string;
  messageValues?: Record<string, string>;
};

export type ToastProps = Omit<NotificationProps, 'testId' | 'title' | 'body'> &
  IntlToastProps & { options?: ExternalToast };

export type ToastScope = 'auth' | 'app';
const scopedToastIds = new Map<ToastScope, Set<string | number>>();

export const dismissToast = (id: string | number) => {
  T.dismiss(id);
  scopedToastIds.forEach((ids) => ids.delete(id));
};

export const dismissToastScope = (scope: ToastScope) => {
  const ids = scopedToastIds.get(scope);
  if (ids) {
    ids.forEach((id) => T.dismiss(id));
    scopedToastIds.delete(scope);
  }
};
/**
 * The global function to call for toasts, each call will make a toast appear,
 * the parameters are the Notification component props
 *
 * @see {@link https://sonner.emilkowal.ski/ Sonner}
 *
 * @example
 * toast({
 *   variant: "information",
 *   title: "title", //Converts intl id to title
 *   message: "this is the body", //Converts intl id to message
 *   // button props
 *   cta: { testId: "cta", children: "CTA" },
 * })
 */
export const toast = (
  { options, title, titleValues, message, messageValues, ...props }: ToastProps,
  scope?: ToastScope,
) => {
  const id = T.custom(
    (t) => (
      <Notification
        {...props}
        //@ts-expect-error
        title={<FormattedMessage id={title} values={titleValues} />}
        body={message ? <FormattedMessage id={message} values={messageValues} /> : undefined}
        onCloseClick={() => T.dismiss(t)}
        testId={`toast-${t}`}
      />
    ),
    {
      ...options,
      onDismiss: (toast) => {
        options?.onDismiss?.(toast);
        if (scope) {
          scopedToastIds.get(scope)?.delete(toast.id);
        }
      },
      onAutoClose: (toast) => {
        options?.onAutoClose?.(toast);
        if (scope) {
          scopedToastIds.get(scope)?.delete(toast.id);
        }
      },
    },
  );

  if (scope) {
    if (!scopedToastIds.has(scope)) {
      scopedToastIds.set(scope, new Set());
    }
    scopedToastIds.get(scope)!.add(id);
  }
  return id;
};
